<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { ...base, unelevated: true, split: true, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 split button with a primary action and a dropdown chevron for additional options.
 *
 * @status recommended
 * @group actions
 * @useWhen Providing a primary action button with a secondary dropdown for related variants
 * @see https://quasar.dev/vue-components/button-dropdown Quasar q-btn-dropdown API
 */
defineOptions({ name: 'HrSplitBtn' });

const props = defineProps({
  /**
   * Visual emphasis variant derived from the M3 hierarchy.
   * @values filled, tonal, elevated, outlined, text
   */
  variant: {
    type: String,
    default: 'filled',
    validator: (v) => ['filled', 'tonal', 'elevated', 'outlined', 'text'].includes(v),
  },
  /**
   * Size of the button, which affects padding and font size.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * The text label of the primary action button (left side).
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * The icon to display on the primary action button.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Whether the button is disabled.
   */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the button is in a loading state.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Override the dropdown caret icon.
   */
  dropdownIcon: {
    type: String,
    default: null,
  },
  /**
   * Prevent the caret icon from rotating when the menu opens.
   */
  noIconAnimation: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the button for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-split-btn',
  `hr-split-btn--${props.variant}`,
  `hr-split-btn--size-${props.size}`,
]);

const effectiveProps = computed(() => {
  const base = {
    split: true,
    ...(props.label !== null && { label: props.label }),
    ...(props.icon !== null && { icon: props.icon }),
    ...(props.dropdownIcon !== null && { dropdownIcon: props.dropdownIcon }),
    noIconAnimation: props.noIconAnimation,
    disable: props.disable,
    loading: props.loading,
  };

  if (hasTheme.value) {
    return { ...base, unelevated: true, contentClass: 'hr-split-btn__popup', ...attrs };
  }
  return { ...base, size: props.size, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-btn-dropdown
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
  >
    <!-- @slot Dropdown menu content, typically a list of HrMenuItem components. -->
    <slot />
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/*
 * In split mode q-btn-dropdown renders as a q-btn-group container div with two
 * inner q-btn elements. The outer container gets the pill shape; the inner
 * buttons share variant color and get half-pill corners where they face outward,
 * square corners where they meet.
 */

.hr-split-btn {
  border-radius: var(--app-shape-corner-full);
  overflow: hidden;
}

/* ── Variant colors (applied to inner buttons) ─────────────────── */

.hr-split-btn--filled :deep(.q-btn) {
  background: var(--app-primary);
  color: var(--app-on-primary);
}

.hr-split-btn--tonal :deep(.q-btn) {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-split-btn--elevated :deep(.q-btn) {
  background: var(--app-surface-container-low);
  color: var(--app-primary);
}

.hr-split-btn--elevated {
  box-shadow: var(--app-elevation-1);
}

.hr-split-btn--outlined :deep(.q-btn) {
  background: transparent;
  color: var(--app-primary);
}

.hr-split-btn--outlined {
  border: 1px solid var(--app-outline);
}

.hr-split-btn--text :deep(.q-btn) {
  background: transparent;
  color: var(--app-primary);
}

/* ── Inner button corners ───────────────────────────────────────── */

.hr-split-btn :deep(.q-btn:first-child) {
  border-radius: var(--app-shape-corner-full) 0 0 var(--app-shape-corner-full);
}

.hr-split-btn :deep(.q-btn:last-child) {
  border-radius: 0 var(--app-shape-corner-full) var(--app-shape-corner-full) 0;
}

/* ── Divider ────────────────────────────────────────────────────── */

/*
 * The divider color matches the content color at a reduced opacity so it
 * reads clearly against each variant's container color.
 */

.hr-split-btn--filled :deep(.q-btn:first-child) {
  border-right: 1px solid color-mix(in srgb, var(--app-on-primary) 38%, transparent);
}

.hr-split-btn--tonal :deep(.q-btn:first-child) {
  border-right: 1px solid color-mix(in srgb, var(--app-on-secondary-container) 38%, transparent);
}

.hr-split-btn--elevated :deep(.q-btn:first-child),
.hr-split-btn--outlined :deep(.q-btn:first-child),
.hr-split-btn--text :deep(.q-btn:first-child) {
  border-right: 1px solid var(--app-outline-variant);
}

/* ── Sizes ──────────────────────────────────────────────────────── */

.hr-split-btn--size-xs :deep(.q-btn) {
  min-height: var(--app-comp-button-container-height-xs);
  padding: 0 var(--app-comp-button-container-padding-xs);
  font-size: var(--app-comp-button-label-text-size-xs);
}

.hr-split-btn--size-sm :deep(.q-btn) {
  min-height: var(--app-comp-button-container-height-sm);
  padding: 0 var(--app-comp-button-container-padding-sm);
  font-size: var(--app-comp-button-label-text-size-sm);
}

.hr-split-btn--size-md :deep(.q-btn) {
  min-height: var(--app-comp-button-container-height-md);
  padding: 0 var(--app-comp-button-container-padding-md);
  font-size: var(--app-comp-button-label-text-size-md);
}

.hr-split-btn--size-lg :deep(.q-btn) {
  min-height: var(--app-comp-button-container-height-lg);
  padding: 0 var(--app-comp-button-container-padding-lg);
  font-size: var(--app-comp-button-label-text-size-lg);
}

.hr-split-btn--size-xl :deep(.q-btn) {
  min-height: var(--app-comp-button-container-height-xl);
  padding: 0 var(--app-comp-button-container-padding-xl);
  font-size: var(--app-comp-button-label-text-size-xl);
}

/* ── The chevron segment ────────────────────────────────────────── */

/*
 * Square, not text-padded. The size rules above put a label's padding on both inner buttons, which
 * is right beside a word and leaves the chevron adrift in a segment half as wide again as it needs
 * — 24dp of padding either side of a 24dp icon at md.
 *
 * Quasar's own arrow margin goes with it: it exists for the unsplit button, where the chevron
 * trails a label inside one button, and here it only pushes the icon off centre.
 */
.hr-split-btn :deep(.q-btn:last-child) {
  aspect-ratio: 1;
  min-width: 0;
  padding-inline: 0;
}

.hr-split-btn :deep(.q-btn-dropdown__arrow) {
  margin: 0;
}

/* ── State layer ────────────────────────────────────────────────── */

/* Keep focus helper behavior intact, and add explicit keyboard focus style */
.hr-split-btn :deep(.q-btn:focus-visible) {
  outline: 2px solid var(--app-primary);
  outline-offset: 2px;
}
.hr-split-btn :deep(.q-btn:focus-visible::before) {
  opacity: 0.12;
}

.hr-split-btn :deep(.q-btn) { text-transform: none; }

.hr-split-btn :deep(.q-btn::before) {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms linear;
}

.hr-split-btn--filled :deep(.q-btn::before)    { background: var(--app-on-primary); }
.hr-split-btn--tonal :deep(.q-btn::before)     { background: var(--app-on-secondary-container); }
.hr-split-btn--elevated :deep(.q-btn::before),
.hr-split-btn--outlined :deep(.q-btn::before),
.hr-split-btn--text :deep(.q-btn::before)      { background: var(--app-primary); }

.hr-split-btn :deep(.q-btn:hover::before)      { opacity: 0.08; }
/* ── Disabled ───────────────────────────────────────────────────── */

.hr-split-btn.disabled :deep(.q-btn) {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-split-btn--outlined.disabled {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

</style>

<style lang="scss">

/*
 * Not scoped — Quasar teleports the dropdown panel outside the component DOM,
 * so scoped styles cannot reach it. The [data-theme] guard ensures these rules
 * only apply when a design system theme is active.
 */

[data-theme] .hr-split-btn__popup {
  background: var(--app-surface-container);
  box-shadow: var(--app-elevation-2);
}

</style>
